import { doc, getDoc } from "firebase/firestore";

// utilities
import { queryClient } from "../../contexts/react-query/query-client";
import { uniqueTargetString } from "wombat-global/src/utilities";
import { getTargetDocPath } from "wombat-global/src/constants";
import { generateFirestoreConnection } from "../firebase.generateFirestoreConnection";

// types
import {
  IClientTarget,
  IConsultantTarget,
  ISiteTarget,
  ITenantTarget,
  OrganizationEntity,
  SiteEntity,
  TenantEntity,
} from "wombat-global/src/typings";
import { useQuery } from "@tanstack/react-query";
import { Time } from "wombat-global/src/utilities/time";

export function fetchClientDocument(target: ISiteTarget): Promise<SiteEntity>;
export function fetchClientDocument(
  target: IConsultantTarget,
): Promise<OrganizationEntity>;
export function fetchClientDocument(
  target: ITenantTarget | { orgId: string; tenantId: string },
): Promise<TenantEntity>;
export function fetchClientDocument(
  target: ITenantTarget | IConsultantTarget | ISiteTarget | IClientTarget,
): Promise<TenantEntity | OrganizationEntity | SiteEntity> {
  if (!window.navigator.onLine) {
    const d = queryClient.getQueryData([
      "fb-doc",
      uniqueTargetString(target),
    ]) as TenantEntity | OrganizationEntity | SiteEntity;
    return Promise.resolve(d);
  }

  return queryClient.fetchQuery({
    queryKey: ["fb-doc", uniqueTargetString(target)],
    queryFn() {
      return generateFirestoreConnection(target)
        .then((fb) => getDoc(doc(fb, getTargetDocPath(target))))
        .then((d) => d.data());
    },
    gcTime: Infinity,
    staleTime: 0,
    networkMode: "offlineFirst",
  });
}

export function useClientDocument(
  target: IClientTarget | ISiteTarget | undefined,
) {
  return useQuery({
    queryKey: ["fb-doc", target ? uniqueTargetString(target) : "noop"],
    queryFn() {
      if (!target) {
        return null;
      }
      return generateFirestoreConnection(target)
        .then((fb) => getDoc(doc(fb, getTargetDocPath(target))))
        .then((d) => d.data());
    },
    gcTime: Infinity,
    staleTime: 10 * Time.mins,
    refetchOnMount(q) {
      return Boolean(q.isStale);
    },
    networkMode: "offlineFirst",
    enabled: Boolean(target),
  });
}
